import type { NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import * as R from 'ramda'
import React, { useCallback, useMemo } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { dragEnd, dragMove, dragStart, updateBlock } from 'services/Store/Project/actions'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { updateCoordinates, setHeight, setWidth } from 'services/Store/Project/mutators/node'
import { mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode, MapType } from 'services/Store/Project/types'
import store from 'services/Store/store'

import { safeParseCoords } from '../../formControls/Coords/utils'
import { safeParsePxSizes } from '../../formControls/Sizes/utils'
import { ResizeControlActionUiEnum } from '../enums'
import type { ICoordinates, IFullCoord, IGuide } from '../types'
import {
  ABSOLUTE_OFFSET,
  calcAbsoluteGuides,
  CALCULATE_COORD,
  findNodeRef,
  getGroupCoords,
  isOffset,
  remapNodesRelativeToCoords,
} from '../utils'
import * as s from './Absolute.module.scss'

interface ITypeMapItem {
  placement:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
  translateX: string
  translateY: string
  cursor: React.CSSProperties['cursor']
  fill: Array<'top' | 'bottom' | 'left' | 'right'>
}

const TYPE_MAP: Record<ResizeControlActionUiEnum, ITypeMapItem> = {
  [ResizeControlActionUiEnum.TOP_LEFT]: {
    placement: 'topLeft',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nwse-resize',
    fill: ['top', 'left'],
  },
  [ResizeControlActionUiEnum.TOP]: {
    placement: 'top',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ns-resize',
    fill: ['top'],
  },
  [ResizeControlActionUiEnum.TOP_RIGHT]: {
    placement: 'topRight',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nesw-resize',
    fill: ['top', 'right'],
  },
  [ResizeControlActionUiEnum.RIGHT]: {
    placement: 'right',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ew-resize',
    fill: ['right'],
  },
  [ResizeControlActionUiEnum.BOTTOM_RIGHT]: {
    placement: 'bottomRight',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nwse-resize',
    fill: ['bottom', 'right'],
  },
  [ResizeControlActionUiEnum.BOTTOM]: {
    placement: 'bottom',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ns-resize',
    fill: ['bottom'],
  },
  [ResizeControlActionUiEnum.BOTTOM_LEFT]: {
    placement: 'bottomLeft',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nesw-resize',
    fill: ['bottom', 'left'],
  },
  [ResizeControlActionUiEnum.LEFT]: {
    placement: 'left',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ew-resize',
    fill: ['left'],
  },
} as const

interface ICoordResizeControlProps {
  type: ResizeControlActionUiEnum
  selected: NodeV2[]
  node: NodeV2 | null
  mode: IBlockMode
  block: Block
}

const CoordResizeControl: React.FC<ICoordResizeControlProps> = ({
  type,
  selected,
  mode,
  block,
  node,
}) => {
  const dispatch = useProjectDispatch()
  const { deviceMode } = mode
  const { cursor, ...portalProps } = TYPE_MAP[type]
  const isFixed = useMemo(() => {
    if (selected.length !== 1) {
      return false
    }
    const style = mergeNodeStyle(selected[0].style, deviceMode)
    const coords = safeParseCoords(style.coords)
    const requiredProps = TYPE_MAP[type].fill
    return requiredProps.every((prop) => typeof coords[prop] === 'number')
  }, [selected, deviceMode, type])

  const onMouseDown = useCallback(
    (downEvent: React.MouseEvent) => {
      downEvent.stopPropagation()
      if (downEvent.button !== 0) {
        return
      }
      const area = getGroupCoords(node, selected, deviceMode)
      const parentStyles = mergeNodeStyle(node?.style || {}, deviceMode)

      let raf: number | null = null
      let isDragging = false
      let placement = R.clone(area)
      let initialCoord: ICoordinates | null = null
      const destBox = findNodeRef(node?.id)?.getBoundingClientRect() || null
      const targetIds = selected.map((node) => node.id)
      const areas = selected
        .map((node) => ({ id: node.id, style: mergeNodeStyle(node.style, deviceMode) }))
        .map(({ id, style }) => {
          const [topMargin, rightMargin, bottomMargin, leftMargin] = safeParsePxSizes(style.margin)
          const coords = safeParseCoords(style.coords)
          return {
            top: coords.top,
            left: coords.left,
            bottom: coords.bottom,
            right: coords.right,
            width: findNodeRef(id)?.getBoundingClientRect().width || 0,
            height: findNodeRef(id)?.getBoundingClientRect().height || 0,
            offset: [topMargin || 0, rightMargin || 0, bottomMargin || 0, leftMargin || 0] as [
              number,
              number,
              number,
              number,
            ],
          }
        })
      const guides =
        node && destBox
          ? calcAbsoluteGuides(
              node,
              block,
              deviceMode,
              destBox,
              parentStyles,
              selected.map((t) => t.id),
            )
          : []
      const horizontalGuides = guides.filter((g) => g.y1 === g.y2)
      const verticalGuides = guides.filter((g) => g.x1 === g.x2)

      const offsets = safeParsePxSizes(parentStyles.margin)
      const topOffset = offsets[0] || 0
      // const rightOffset = offsets[1] || 0
      // const bottomOffset = offsets[2] || 0
      const leftOffset = offsets[3] || 0
      initialCoord = { x: downEvent.clientX, y: downEvent.clientY }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (raf) {
          return
        }
        raf = requestAnimationFrame(() => {
          raf = null
          if (!isDragging && initialCoord && isOffset(initialCoord, moveEvent) && node) {
            dispatch(
              dragStart({
                initialCoords: initialCoord,
                sourceId: node.id,
                box: null,
                targetIds,
                type,
              }),
            )
            isDragging = true
            document.getSelection()?.removeAllRanges()
          }
          const nodeBox = findNodeRef(node?.id || '')?.getBoundingClientRect()

          if (isDragging && parentStyles && initialCoord && node && nodeBox) {
            const activeGuides = guides.filter((g) => g.alwaysShow) || []
            const currentCoords = { x: moveEvent.clientX, y: moveEvent.clientY }
            const { x, guides: vGuides } = verticalGuides.reduce(
              (nearest, g) => {
                const min = Math.abs(g.x1 - leftOffset - (currentCoords.x - nodeBox.x))
                const isMin = min < nearest.dist
                return {
                  ...nearest,
                  ...(isMin && {
                    dist: min,
                    x: min < ABSOLUTE_OFFSET ? g.x1 - leftOffset : currentCoords.x - nodeBox.x,
                    guides: [
                      {
                        ...g,
                        helper:
                          min >= ABSOLUTE_OFFSET &&
                          [
                            ResizeControlActionUiEnum.LEFT,
                            ResizeControlActionUiEnum.RIGHT,
                            ResizeControlActionUiEnum.BOTTOM_LEFT,
                            ResizeControlActionUiEnum.BOTTOM_RIGHT,
                            ResizeControlActionUiEnum.TOP_LEFT,
                            ResizeControlActionUiEnum.TOP_RIGHT,
                          ].includes(type)
                            ? {
                                y1: topOffset + (currentCoords.y - nodeBox.y),
                                y2: topOffset + (currentCoords.y - nodeBox.y),
                                x1: g.x1,
                                x2: leftOffset + currentCoords.x - nodeBox.x,
                                text: min,
                              }
                            : null,
                      },
                    ],
                  }),
                }
              },
              { x: currentCoords.x - nodeBox.x - leftOffset, dist: Infinity, guides: [] } as {
                x: number
                dist: number
                guides: IGuide[]
              },
            )

            const { y, guides: hGuides } = horizontalGuides.reduce(
              (nearest, g) => {
                const min = Math.abs(g.y1 - topOffset - (currentCoords.y - nodeBox.y))
                const isMin = min < nearest.dist
                return {
                  ...nearest,
                  ...(isMin && {
                    dist: min,
                    y: min < ABSOLUTE_OFFSET ? g.y1 - topOffset : currentCoords.y - nodeBox.y,
                    bottom: undefined,
                    guides: [
                      {
                        ...g,
                        helper:
                          min >= ABSOLUTE_OFFSET &&
                          [
                            ResizeControlActionUiEnum.TOP,
                            ResizeControlActionUiEnum.BOTTOM,
                            ResizeControlActionUiEnum.BOTTOM_LEFT,
                            ResizeControlActionUiEnum.BOTTOM_RIGHT,
                            ResizeControlActionUiEnum.TOP_LEFT,
                            ResizeControlActionUiEnum.TOP_RIGHT,
                          ].includes(type)
                            ? {
                                x1: leftOffset + (currentCoords.x - nodeBox.x),
                                x2: leftOffset + (currentCoords.x - nodeBox.x),
                                y1: g.y1,
                                y2: topOffset + currentCoords.y - nodeBox.y,
                                text: min,
                              }
                            : null,
                      },
                    ],
                  }),
                }
              },
              { y: currentCoords.y - nodeBox.y - topOffset, dist: Infinity, guides: [] } as {
                y: number
                dist: number
                guides: IGuide[]
              },
            )

            activeGuides.unshift(...hGuides, ...vGuides)

            placement = CALCULATE_COORD[type](
              { x: nodeBox.x + x, y: nodeBox.y + y },
              placement,
              node.id,
            )
            const placeholder = remapNodesRelativeToCoords(nodeBox, type, areas, area, placement)

            dispatch(
              dragMove({
                zone: 'canvas',
                destinationId: node.id,
                currentCoords,
                coords: selected.reduce((acc, node, idx) => {
                  acc[node.id] = placeholder[idx]
                  return acc
                }, {} as MapType<IFullCoord>),
                flex: null,
                index: null,
                areas: null,
                guides: R.uniqBy(({ id }) => id, activeGuides),
              }),
            )
          }
        })
      }

      const handleMouseUp = (upEvent: MouseEvent) => {
        upEvent.stopPropagation()
        document.removeEventListener('mouseup', handleMouseUp, true)
        document.removeEventListener('mousemove', handleMouseMove, true)
        const state = store.getState().project
        const dnd = state.state.editor.dnd
        if (isDragging) {
          const newBlock = R.compose(
            ...(selected.map((node) =>
              R.compose(
                setWidth(node.id, deviceMode, dnd?.coords?.[node.id]?.width),
                setHeight(node.id, deviceMode, dnd?.coords?.[node.id]?.height),
                updateCoordinates(node.id, deviceMode, dnd?.coords?.[node.id] || {}),
              ),
            ) as [(block: Block) => Block]),
          )(block)
          dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
        }
        dispatch(dragEnd())
        initialCoord = null
        isDragging = false
      }

      document.addEventListener('mouseup', handleMouseUp, true)
      document.addEventListener('mousemove', handleMouseMove, true)
    },
    [selected, deviceMode, node, dispatch, type, block],
  )

  // const handleClick = useCallback(() => {
  //   const area = getGroupCoords(node, selected, deviceMode)
  //   const fill = TYPE_MAP[type].fill
  //   const reset = TYPE_MAP[type].anti
  //   const newBlock = R.compose(
  //     ...(selected.map((node) =>
  //       R.compose(
  //         fill.includes('top') ? setTop(node.id, deviceMode, area.top) : R.identity<Block>,
  //         fill.includes('bottom') ? setBottom(node.id, deviceMode, area.bottom) : R.identity<Block>,
  //         fill.includes('left') ? setLeft(node.id, deviceMode, area.left) : R.identity<Block>,
  //         fill.includes('right') ? setRight(node.id, deviceMode, area.right) : R.identity<Block>,
  //         ...(reset.map((prop) => resetStyle(node.id, deviceMode, prop)) as [
  //           (block: Block) => Block,
  //         ]),
  //       ),
  //     ) as [(block: Block) => Block]),
  //   )(block)
  //   dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  // }, [dispatch, deviceMode, block, selected, type, node])

  const handleDoubleClick = useCallback(() => {
    const area = getGroupCoords(node, selected, deviceMode)
    const fill = TYPE_MAP[type].fill
    const newBlock = R.compose(
      ...(selected.map((node) => updateCoordinates(node.id, deviceMode, R.pick(fill, area))) as [
        (block: Block) => Block,
      ]),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }, [dispatch, deviceMode, node, selected, type, block])

  return (
    <AbsolutePortal {...portalProps} chain='off' name={type}>
      <div
        className={s.resize}
        onDoubleClick={handleDoubleClick}
        onMouseDown={onMouseDown}
        style={{ cursor }}
      >
        <div className={cn(s.control, { [s.lock]: isFixed })} />
      </div>
    </AbsolutePortal>
  )
}

export default CoordResizeControl
